import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  PlusIcon,
  UserPlusIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  ClockIcon,
  Bars3Icon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/state/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useTheme } from '@/state/ThemeContext';
import { useNotifications, useMarkNotificationRead, NotificationStub } from '@/services/api';
import { useToasts } from '@/state/ToastContext';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, roleMeta, logout } = useAuth();
  const { pushToast } = useToasts();
  const navigate = useNavigate();
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const searchResults = useGlobalSearch(searchQuery);
  const {
    data: notificationFeedPayload,
    isLoading: notificationsLoading,
  } = useNotifications({ pageSize: 50 });
  const notificationFeed = useMemo(
    () => notificationFeedPayload?.value ?? [],
    [notificationFeedPayload?.value],
  );
  const markNotificationRead = useMarkNotificationRead();
  const actionableNotifications = useMemo(
    () =>
      notificationFeed.filter((notification) => {
        const status = notification.status?.toLowerCase() ?? '';
        return !status || ['pending', 'queued', 'scheduled', 'failed', 'error'].includes(status);
      }),
    [notificationFeed],
  );
  const notificationBadge = actionableNotifications.length
    ? actionableNotifications.length > 99
      ? '99+'
      : String(actionableNotifications.length)
    : null;
  const resolvedNotifications = useMemo(
    () => notificationFeed.slice(0, 20),
    [notificationFeed],
  );
  const unreadDropdownNotifications = useMemo(
    () =>
      resolvedNotifications.filter((notification) => (notification.status ?? '').toLowerCase() !== 'read'),
    [resolvedNotifications],
  );

  const handleNotificationRead = async (notification: NotificationStub) => {
    if ((notification.status ?? '').toLowerCase() === 'read') return;
    try {
      await markNotificationRead.mutateAsync({ notificationId: notification.id });
      pushToast({ tone: 'success', message: '通知已標記為已讀' });
    } catch (err) {
      pushToast({
        tone: 'error',
        message: '無法標記通知',
        detail: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleMarkAllNotifications = async () => {
    if (!unreadDropdownNotifications.length) return;
    try {
      for (const notification of unreadDropdownNotifications) {
        await markNotificationRead.mutateAsync({ notificationId: notification.id });
      }
      pushToast({
        tone: 'success',
        message:
          unreadDropdownNotifications.length === 1
            ? '已標記 1 則通知已讀'
            : `已標記 ${unreadDropdownNotifications.length} 則通知已讀`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        message: '無法批次標記通知',
        detail: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const formatNotificationTime = (value?: string | null) => {
    if (!value) return '剛剛';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    const diffMs = Date.now() - parsed.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 1) return '剛剛';
    if (diffMinutes < 60) return `${diffMinutes} 分鐘前`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} 小時前`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 7) return `${diffDays} 天前`;
    return parsed.toLocaleString('zh-TW', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusVisual = (status?: string | null) => {
    const normalized = status?.toLowerCase();
    if (!normalized) {
      return { label: '佇列中', className: 'bg-amber-50 text-amber-600' };
    }
    if (['sent', 'delivered', 'success'].includes(normalized)) {
      return { label: '已送達', className: 'bg-emerald-50 text-emerald-600' };
    }
    if (['failed', 'error', 'bounced'].includes(normalized)) {
      return { label: '失敗', className: 'bg-rose-100 text-rose-600' };
    }
    if (['scheduled', 'pending', 'queued'].includes(normalized)) {
      return { label: '排程中', className: 'bg-indigo-50 text-indigo-600' };
    }
    return { label: status, className: 'bg-slate-100 text-slate-600' };
  };

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedSearchIndex(0);
  }, [searchResults]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'k',
        metaKey: true,
        callback: () => {
          searchInputRef.current?.focus();
          setShowSearchResults(true);
        },
        description: '⌘K to search',
      },
      {
        key: 'Escape',
        callback: () => {
          setShowQuickActions(false);
          setShowNotifications(false);
          setShowSearchResults(false);
          searchInputRef.current?.blur();
        },
        description: 'ESC to close',
        preventDefault: false,
      },
      {
        key: 'ArrowDown',
        callback: () => {
          if (showSearchResults && searchResults.length > 0) {
            setSelectedSearchIndex((prev) => 
              prev < searchResults.length - 1 ? prev + 1 : 0
            );
          }
        },
        description: 'Navigate search results',
        preventDefault: showSearchResults,
      },
      {
        key: 'ArrowUp',
        callback: () => {
          if (showSearchResults && searchResults.length > 0) {
            setSelectedSearchIndex((prev) => 
              prev > 0 ? prev - 1 : searchResults.length - 1
            );
          }
        },
        description: 'Navigate search results',
        preventDefault: showSearchResults,
      },
      {
        key: 'Enter',
        callback: () => {
          if (showSearchResults && searchResults.length > 0) {
            const selectedResult = searchResults[selectedSearchIndex];
            if (selectedResult) {
              handleSearchResultClick(selectedResult.path);
            }
          }
        },
        description: 'Select search result',
        preventDefault: showSearchResults,
      },
    ],
  });

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target as Node)) {
        setShowQuickActions(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchResultClick = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };
  
  const today = new Intl.DateTimeFormat('zh-Hant', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date());

  const initials = useMemo(() => {
    if (!user?.name) return 'LK';
    const parts = user.name.trim().split(/\s+/);
    return parts
      .map((part) => part[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('');
  }, [user?.name]);

  const quickActions = [
    { icon: UserPlusIcon, label: '新增病患', action: () => navigate('/patients') },
    { icon: CalendarIcon, label: '新增預約', action: () => navigate('/appointments') },
    { icon: ClipboardDocumentListIcon, label: '新增照護計畫', action: () => navigate('/care-plans') },
  ];

  return (
  <header className="sticky top-0 z-30 border-b border-white/60 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          {/* Mobile hamburger menu */}
          <button
            onClick={onMenuClick}
            className="rounded-lg p-2 hover:bg-slate-100 lg:hidden touch-manipulation"
            aria-label="開啟選單"
          >
            <Bars3Icon className="h-6 w-6 text-slate-600 dark:text-slate-300" />
          </button>
          
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate">
              歡迎回來，陳醫師
              <span className="ml-2 sm:ml-3 text-xs sm:text-sm font-normal text-slate-500">• {today}</span>
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 hidden sm:block">掌握院內營運並協調今日的照護工作。</p>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-2 sm:gap-4 md:flex-none">
          {/* Global Search */}
          <div className="relative flex-1 md:flex-none" ref={searchRef}>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              ref={searchInputRef}
              type="search"
              placeholder="搜尋 (⌘K)"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              onFocus={() => setShowSearchResults(true)}
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-2 pl-10 pr-4 text-sm text-slate-700 dark:text-slate-200 shadow-sm focus:border-brand-blue focus:ring-brand-blue md:w-64 touch-manipulation"
            />
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchQuery.length >= 2 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg md:left-auto md:w-96">
                {searchResults.length > 0 ? (
                  <>
                    <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-2">
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        找到 {searchResults.length} 個結果
                      </p>
                    </div>
                    {searchResults.map((result, index) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleSearchResultClick(result.path)}
                        className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-0 ${
                          index === selectedSearchIndex 
                            ? 'bg-brand-blue/5 border-l-2 border-l-brand-blue' 
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
                          result.type === 'patient' ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-green-50 dark:bg-green-900/30'
                        }`}>
                          {result.type === 'patient' ? (
                            <UserIcon className="h-5 w-5 text-blue-600" />
                          ) : (
                            <ClockIcon className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{result.title}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{result.subtitle}</p>
                        </div>
                      </button>
                    ))}
                    <div className="border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 px-4 py-2">
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">
                        <kbd className="mx-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-semibold">↑</kbd>
                        <kbd className="mx-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-semibold">↓</kbd>
                        導航
                        <kbd className="ml-2 mr-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-semibold">Enter</kbd>
                        選擇
                        <kbd className="ml-2 mr-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-semibold">Esc</kbd>
                        關閉
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">找不到符合的結果</p>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">請嘗試其他關鍵字</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Quick Actions Dropdown */}
          <div className="relative" ref={quickActionsRef}>
            <button
              type="button"
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="flex items-center gap-2 rounded-xl border border-brand-blue bg-brand-blue px-3 sm:px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-blue/90 touch-manipulation min-h-[44px]"
              aria-label="快速操作"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden sm:inline">新增</span>
            </button>
            
            {showQuickActions && (
              <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-slate-200 bg-white shadow-lg">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      action.action();
                      setShowQuickActions(false);
                    }}
                    className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-slate-50 last:border-0"
                  >
                    <action.icon className="h-5 w-5 text-brand-blue" />
                    <span className="font-medium">{action.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Notifications Dropdown */}
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-slate-500 dark:text-slate-400 shadow-sm hover:text-brand-blue touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="通知"
            >
              <BellIcon className="h-5 w-5" />
              {notificationBadge && (
                <span className="absolute -right-1 -top-1 inline-flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
                  {notificationBadge}
                </span>
              )}
            </button>
            
            {showNotifications && (
              <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
                <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-3">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">通知中心</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {notificationsLoading ? '同步通知資料…' : `${notificationFeed.length} 則通知`}
                  </p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notificationsLoading ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                      載入通知中…
                    </div>
                  ) : resolvedNotifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400 dark:text-slate-500">
                      目前沒有最新通知
                    </div>
                  ) : (
                    resolvedNotifications.map((notification) => {
                      const statusVisual = getStatusVisual(notification.status);
                      const isRead = (notification.status ?? '').toLowerCase() === 'read';
                      return (
                        <div
                          key={notification.id}
                          className="border-b border-slate-100 dark:border-slate-700 px-4 py-3 transition hover:bg-slate-50 dark:hover:bg-slate-700/40 last:border-0"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {notification.channel?.toUpperCase() ?? '通知'}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {notification.to ?? '未指定收件人'}
                              </p>
                              <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{notification.message}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusVisual.className}`}
                              >
                                {statusVisual.label}
                              </span>
                              {!isRead && (
                                <button
                                  type="button"
                                  className="text-[10px] font-semibold text-brand-blue hover:underline disabled:opacity-40"
                                  onClick={() => handleNotificationRead(notification)}
                                  disabled={markNotificationRead.isPending}
                                >
                                  標記已讀
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="mt-2 text-[10px] text-slate-400 dark:text-slate-500">
                            {formatNotificationTime(notification.createdAt ?? notification.scheduledAt)}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="flex flex-col gap-2 border-t border-slate-100 dark:border-slate-700 px-4 py-2">
                  <button
                    type="button"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                    onClick={handleMarkAllNotifications}
                    disabled={unreadDropdownNotifications.length === 0 || markNotificationRead.isPending}
                  >
                    {markNotificationRead.isPending
                      ? '處理中…'
                      : unreadDropdownNotifications.length > 0
                        ? `將 ${unreadDropdownNotifications.length} 則標記為已讀`
                        : '沒有未讀通知'}
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-lg px-3 py-2 text-xs font-medium text-brand-blue hover:bg-brand-blue/5 dark:hover:bg-brand-blue/10"
                    onClick={() => {
                      setShowNotifications(false);
                      navigate('/notifications');
                    }}
                  >
                    查看所有通知
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Theme Toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-slate-500 dark:text-slate-400 shadow-sm hover:text-brand-blue touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="切換主題"
          >
            {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
          <div className="hidden sm:flex items-center gap-3 rounded-2xl border border-white/80 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 shadow-soft">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-sky text-white ring-4 ring-white">
              <div className="flex h-full w-full items-center justify-center text-lg font-semibold">
                {initials}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{user?.name ?? '團隊成員'}</p>
              {roleMeta && <p className={`text-xs font-semibold ${roleMeta.accent}`}>{roleMeta.label}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 shadow-soft transition hover:border-rose-200 hover:text-rose-600 dark:hover:border-rose-700 dark:hover:text-rose-400 touch-manipulation min-h-[44px]"
            aria-label="登出"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            <span className="hidden xl:inline">登出</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
